import React, { useState, useEffect } from 'react';
import { assetsApi, formatApiError } from '../../services/api';
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  Filter,
  Monitor,
  Cpu,
  Globe,
  Printer,
  Smartphone,
  Package,
  MoreHorizontal,
  Pencil,
  Trash2,
  Loader2
} from 'lucide-react';

const typeIcons = {
  hardware: Cpu,
  software: Package,
  network: Globe,
  printer: Printer,
  mobile: Smartphone,
  other: Monitor
};

export default function AssetsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({
    name: '',
    type: 'hardware',
    serial_number: '',
    ip_address: '',
    status: 'active',
    location: '',
    owner: '',
    purchase_date: '',
    notes: ''
  });

  const canManage = user?.role && ['super_admin', 'admin', 'technician'].includes(user.role);
  const canDelete = user?.role && ['super_admin', 'admin'].includes(user.role);

  useEffect(() => {
    fetchAssets();
  }, [filterType, filterStatus]);

  const fetchAssets = async () => {
    try {
      const params = {};
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      if (searchQuery) params.search = searchQuery;
      const res = await assetsApi.getAll(params);
      setAssets(res.data);
    } catch (error) {
      console.error('Fetch assets error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchAssets();
  };

  const openCreateModal = () => {
    setEditingAsset(null);
    setForm({
      name: '', type: 'hardware', serial_number: '', ip_address: '',
      status: 'active', location: '', owner: '', purchase_date: '', notes: ''
    });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (asset) => {
    setEditingAsset(asset);
    setForm({
      name: asset.name || '',
      type: asset.type || 'hardware',
      serial_number: asset.serial_number || '',
      ip_address: asset.ip_address || '',
      status: asset.status || 'active',
      location: asset.location || '',
      owner: asset.owner || '',
      purchase_date: asset.purchase_date || '',
      notes: asset.notes || ''
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      if (editingAsset) {
        await assetsApi.update(editingAsset.id, form);
      } else {
        await assetsApi.create(form);
      }
      setShowModal(false);
      setLoading(true);
      fetchAssets();
    } catch (error) {
      setFormError(formatApiError(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm(t('delete') + '?')) return;
    try {
      await assetsApi.delete(assetId);
      setLoading(true);
      fetchAssets();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-[#16A34A] border-[#16A34A]',
      maintenance: 'bg-amber-100 text-[#D97706] border-[#D97706]',
      retired: 'bg-gray-100 text-[#71717A] border-[#71717A]'
    };
    return styles[status] || styles.active;
  };

  const getTypeBadge = (type) => {
    const styles = {
      hardware: 'bg-blue-100 text-[#2A52C4] border-[#2A52C4]',
      software: 'bg-purple-100 text-purple-700 border-purple-700',
      network: 'bg-cyan-100 text-cyan-700 border-cyan-700',
      printer: 'bg-orange-100 text-orange-700 border-orange-700',
      mobile: 'bg-pink-100 text-pink-700 border-pink-700',
      other: 'bg-gray-100 text-[#71717A] border-[#71717A]'
    };
    return styles[type] || styles.other;
  };

  const filteredAssets = assets.filter(asset => {
    if (searchQuery) {
      const s = searchQuery.toLowerCase();
      return (
        asset.name?.toLowerCase().includes(s) ||
        asset.serial_number?.toLowerCase().includes(s) ||
        asset.owner?.toLowerCase().includes(s) ||
        asset.location?.toLowerCase().includes(s) ||
        asset.ip_address?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2A52C4]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('asset_list')}
          </h1>
          <p className="text-[#71717A] mt-1">{filteredAssets.length} asset</p>
        </div>
        {canManage && (
          <Button onClick={openCreateModal} className="bg-[#2A52C4] hover:bg-[#2A52C4]/90 text-white">
            <Plus className="w-4 h-4 mr-2" />
            {t('create_asset')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border border-[#E4E4E7] shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <Input
                placeholder={t('search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9 border-[#E4E4E7]"
              />
            </div>
            <Select value={filterType || 'all'} onValueChange={(v) => { setFilterType(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-full sm:w-[180px] border-[#E4E4E7]">
                <Filter className="w-4 h-4 mr-2 text-[#71717A]" />
                <SelectValue placeholder={t('asset_type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('asset_type')}</SelectItem>
                <SelectItem value="hardware">{t('hardware')}</SelectItem>
                <SelectItem value="software">{t('software')}</SelectItem>
                <SelectItem value="network">{t('network')}</SelectItem>
                <SelectItem value="printer">{t('printer')}</SelectItem>
                <SelectItem value="mobile">{t('mobile')}</SelectItem>
                <SelectItem value="other">{t('other')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus || 'all'} onValueChange={(v) => { setFilterStatus(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-full sm:w-[180px] border-[#E4E4E7]">
                <SelectValue placeholder={t('status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('status')}</SelectItem>
                <SelectItem value="active">{t('active')}</SelectItem>
                <SelectItem value="maintenance">{t('maintenance')}</SelectItem>
                <SelectItem value="retired">{t('retired')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {filteredAssets.length === 0 ? (
          <div className="text-center py-12 text-[#71717A]">
            <Monitor className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nessun asset trovato</p>
          </div>
        ) : (
          filteredAssets.map((asset) => {
            const TypeIcon = typeIcons[asset.type] || Monitor;
            return (
              <div key={asset.id} className="bg-white rounded-lg border border-[#E4E4E7] p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <TypeIcon className="w-5 h-5 text-[#2A52C4]" />
                    <p className="font-medium text-[#0F172A] text-sm">{asset.name}</p>
                  </div>
                  <Badge className={`${getStatusBadge(asset.status)} border text-xs`}>
                    {t(asset.status)}
                  </Badge>
                </div>
                <div className="text-xs text-[#71717A] space-y-1 mt-2">
                  {asset.serial_number && <p>{t('serial_number')}: {asset.serial_number}</p>}
                  {asset.ip_address && <p>{t('ip_address')}: {asset.ip_address}</p>}
                  {asset.owner && <p>{t('owner')}: {asset.owner}</p>}
                  {asset.location && <p>{t('location')}: {asset.location}</p>}
                </div>
                {canManage && (
                  <div className="flex gap-2 mt-3 pt-2 border-t border-[#F4F4F5]">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(asset)} className="text-[#2A52C4]">
                      <Pencil className="w-3 h-3 mr-1" /> {t('edit')}
                    </Button>
                    {canDelete && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(asset.id)} className="text-[#DC2626]">
                        <Trash2 className="w-3 h-3 mr-1" /> {t('delete')}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block border border-[#E4E4E7] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                <TableHead className="font-medium">{t('name')}</TableHead>
                <TableHead className="font-medium">{t('asset_type')}</TableHead>
                <TableHead className="font-medium">{t('serial_number')}</TableHead>
                <TableHead className="font-medium">{t('ip_address')}</TableHead>
                <TableHead className="font-medium">{t('owner')}</TableHead>
                <TableHead className="font-medium">{t('location')}</TableHead>
                <TableHead className="font-medium">{t('status')}</TableHead>
                {canManage && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAssets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManage ? 8 : 7} className="text-center py-12">
                    <div className="text-[#71717A]">
                      <Monitor className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nessun asset trovato</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAssets.map((asset) => {
                  const TypeIcon = typeIcons[asset.type] || Monitor;
                  return (
                    <TableRow key={asset.id} className="hover:bg-[#F4F4F5] border-b border-[#E4E4E7]">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TypeIcon className="w-4 h-4 text-[#2A52C4]" />
                          <span className="font-medium text-[#0F172A]">{asset.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getTypeBadge(asset.type)} border text-xs`}>
                          {t(asset.type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-mono">{asset.serial_number || '-'}</TableCell>
                      <TableCell className="text-sm font-mono">{asset.ip_address || '-'}</TableCell>
                      <TableCell className="text-sm">{asset.owner || '-'}</TableCell>
                      <TableCell className="text-sm">{asset.location || '-'}</TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadge(asset.status)} border text-xs`}>
                          {t(asset.status)}
                        </Badge>
                      </TableCell>
                      {canManage && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditModal(asset)}>
                                <Pencil className="w-4 h-4 mr-2" /> {t('edit')}
                              </DropdownMenuItem>
                              {canDelete && (
                                <DropdownMenuItem onClick={() => handleDelete(asset.id)} className="text-[#DC2626]">
                                  <Trash2 className="w-4 h-4 mr-2" /> {t('delete')}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[550px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editingAsset ? t('edit_asset') : t('create_asset')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {formError && (
              <div className="p-3 text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md">
                {formError}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#0F172A]">{t('name')} *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="MacBook Pro M3"
                className="h-10 border-[#E4E4E7]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[#0F172A]">{t('asset_type')}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="h-10 border-[#E4E4E7]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hardware">{t('hardware')}</SelectItem>
                    <SelectItem value="software">{t('software')}</SelectItem>
                    <SelectItem value="network">{t('network')}</SelectItem>
                    <SelectItem value="printer">{t('printer')}</SelectItem>
                    <SelectItem value="mobile">{t('mobile')}</SelectItem>
                    <SelectItem value="other">{t('other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[#0F172A]">{t('status')}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="h-10 border-[#E4E4E7]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('active')}</SelectItem>
                    <SelectItem value="maintenance">{t('maintenance')}</SelectItem>
                    <SelectItem value="retired">{t('retired')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[#0F172A]">{t('serial_number')}</Label>
                <Input
                  value={form.serial_number}
                  onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                  placeholder="SN-XXX-12345"
                  className="h-10 border-[#E4E4E7]"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[#0F172A]">{t('ip_address')}</Label>
                <Input
                  value={form.ip_address}
                  onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
                  placeholder="192.168.1.100"
                  className="h-10 border-[#E4E4E7]"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[#0F172A]">{t('owner')}</Label>
                <Input
                  value={form.owner}
                  onChange={(e) => setForm({ ...form, owner: e.target.value })}
                  placeholder="Mario Rossi"
                  className="h-10 border-[#E4E4E7]"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[#0F172A]">{t('location')}</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Ufficio Milano"
                  className="h-10 border-[#E4E4E7]"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#0F172A]">{t('purchase_date')}</Label>
              <Input
                type="date"
                value={form.purchase_date}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                className="h-10 border-[#E4E4E7]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#0F172A]">{t('notes')}</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="border-[#E4E4E7]"
              />
            </div>
            <DialogFooter className="mt-6 pt-4 border-t border-[#E4E4E7]">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-[#E4E4E7]">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-[#2A52C4] hover:bg-[#2A52C4]/90 text-white" disabled={formLoading}>
                {formLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> ...</>
                ) : (
                  t('save')
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
